import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CreateTenantStaffPayload,
  TenantStaffDto,
  TenantUsersService,
} from '../../../shared/services/tenant-users.service';
import { TenantAuthService } from '../../../services/tenant-auth.service';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
} from '../../../shared/admin';

@Component({
  selector: 'app-tenant-staff-page',
  standalone: true,
  imports: [
    FormsModule,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminModalComponent,
  ],
  template: `
    <section>
      <app-admin-page-header>
        @if (canManage()) {
          <ng-container actions>
            <app-admin-button variant="primary" type="button" (click)="openCreate()">Add staff</app-admin-button>
          </ng-container>
        }
      </app-admin-page-header>

      <app-admin-table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (list().length === 0) {
            <tr>
              <td colspan="4">
                <app-admin-empty-state icon="users" title="No staff members" />
              </td>
            </tr>
          }
          @for (u of list(); track u.id) {
            <tr>
              <td>{{ u.email }}</td>
              <td>{{ u.role }}</td>
              <td>{{ u.isActive ? 'Active' : 'Inactive' }}</td>
              <td>
                @if (canManage()) {
                  <app-admin-button variant="secondary" type="button" (click)="toggleActive(u)">
                    {{ u.isActive ? 'Deactivate' : 'Activate' }}
                  </app-admin-button>
                }
              </td>
            </tr>
          }
        </tbody>
      </app-admin-table>

      @if (showForm()) {
        <app-admin-modal title="Add staff" (closed)="showForm.set(false)">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Email</span>
            <input class="cg-input" type="email" [(ngModel)]="form.email" name="email" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Role</span>
            <select class="cg-select" [(ngModel)]="form.role" name="role">
              <option value="FRANCHISE_ADMIN">FRANCHISE_ADMIN</option>
              <option value="FRANCHISE_STAFF">FRANCHISE_STAFF</option>
            </select>
          </label>
          <div class="mt-4 flex gap-2">
            <app-admin-button variant="primary" type="button" (click)="submit()">Create</app-admin-button>
          </div>
        </app-admin-modal>
      }
    </section>
  `,
})
export class TenantStaffPageComponent implements OnInit {
  private readonly tenantUsersService = inject(TenantUsersService);
  private readonly tenantAuth = inject(TenantAuthService);

  readonly list = signal<TenantStaffDto[]>([]);
  readonly showForm = signal(false);
  form: CreateTenantStaffPayload = { email: '', role: 'FRANCHISE_STAFF' };

  readonly canManage = computed(() => this.tenantAuth.currentUser()?.role === 'FRANCHISE_ADMIN');

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.tenantUsersService.list().subscribe({
      next: (data) => this.list.set(data),
    });
  }

  openCreate(): void {
    this.form = { email: '', role: 'FRANCHISE_STAFF' };
    this.showForm.set(true);
  }

  submit(): void {
    this.tenantUsersService.create(this.form).subscribe({
      next: () => {
        this.showForm.set(false);
        this.refresh();
      },
    });
  }

  toggleActive(user: TenantStaffDto): void {
    this.tenantUsersService.updateStatus(user.id, !user.isActive).subscribe({
      next: () => this.refresh(),
    });
  }
}
